import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'

import { httpOk, GET, POST, PUT } from 'helpers/api'
import { formatDate } from "helpers/helpers"

// Подписи событий вебхука — ключи локалей по коду события
const EVENT_LABELS = {
	'shift.opened': 'webhook_event_shift_opened',
	'shift.closed': 'webhook_event_shift_closed',
	'cash.movement': 'webhook_event_cash_movement',
	'cheque.closed': 'webhook_event_cheque_closed',
	'cheque.deleted': 'webhook_event_cheque_deleted',
	'cheque.returned': 'webhook_event_cheque_returned',
	'cheque.manual_discount': 'webhook_event_cheque_manual_discount',
	'order.status_changed': 'webhook_event_order_status_changed',
	'stop_list.changed': 'webhook_event_stop_list_changed',
	'stock.low': 'webhook_event_stock_low',
	'document.posted': 'webhook_event_document_posted',
	'attendance.check_in': 'webhook_event_attendance_check_in',
	'attendance.check_out': 'webhook_event_attendance_check_out',
	'feedback.created': 'webhook_event_feedback_created',
	'client.created': 'webhook_event_client_created',
	'reservation.created': 'webhook_event_reservation_created',
	'reservation.updated': 'webhook_event_reservation_updated',
	'reservation.status_changed': 'webhook_event_reservation_status_changed',
	'occupancy.changed': 'webhook_event_occupancy_changed',
}

// Какой галочкой ключа открывается событие — подсказка у недоступного
const PERMISSION_LABELS = {
	'is_reservations_allowed': 'api_reservations_access',
	'is_reports_allowed': 'api_reports_access',
	'is_attendance_allowed': 'api_attendance_access',
}

/**
 * Исходящий вебхук ключа: куда Mison шлёт события — смены, чеки, заказы ключа, стоп-лист,
 * склад, персонал, гости, брони. Каждое событие открывается своей галочкой ключа
 * (PERMISSION_LABELS). Партнёр проверяет подпись X-Mison-Signature секретом отсюда.
 * Журнал — последние 50 доставок: не дошло — повтор по расписанию или кнопкой.
 */
function IntegrationKeyWebhook({ show, item, onHide }) {
	const { t } = useTranslation()

	const [webhook, setWebhook] = useState({ 'url': '', 'events': [], 'is_active': true })
	const [secret, setSecret] = useState('')
	const [availableEvents, setAvailableEvents] = useState([])
	const [deliveries, setDeliveries] = useState([])

	function applyResponse(data) {
		setWebhook({
			'url': data?.webhook?.url ?? '',
			'events': data?.webhook?.events ?? [],
			'is_active': data?.webhook ? !!data.webhook.is_active : true,
		})
		setSecret(data?.webhook?.secret ?? '')
		setAvailableEvents(data?.available_events ?? [])
		setDeliveries(data?.deliveries ?? [])
	}

	async function getData() {
		const response = await GET(`/external-integrations-keys/${item.id}/webhook`, {}, { loader: true })
		if (httpOk(response)) applyResponse(response.data)
	}

	async function save(regenerateSecret = false) {
		const response = await PUT(`/external-integrations-keys/${item.id}/webhook`, {
			...webhook,
			'regenerate_secret': regenerateSecret,
		}, { loader: true })
		if (httpOk(response)) applyResponse(response.data)
	}

	async function ping() {
		const response = await POST(`/external-integrations-keys/${item.id}/webhook/ping`, {}, { loader: true, notification: false })
		if (!httpOk(response)) return
		if (response.data.ok) {
			toast.success(t('webhook_ping_ok') + (response.data.http_code ? ` (HTTP ${response.data.http_code})` : ''))
		} else {
			toast.error(t('webhook_ping_failed') + ': ' + (response.data.error || response.data.http_code || ''))
		}
	}

	async function retry(delivery) {
		const response = await POST(`/external-integrations-keys/${item.id}/webhook/deliveries/${delivery.id}/retry`, {}, { loader: true })
		if (httpOk(response)) applyResponse(response.data)
	}

	function toggleEvent(event) {
		const events = webhook.events.includes(event)
			? webhook.events.filter(item => item !== event)
			: [...webhook.events, event]
		setWebhook({ ...webhook, 'events': events })
	}

	function returnClassStatus(delivery) {
		if (delivery.status === 'sent') return 'bg-success text-white'
		if (delivery.status === 'failed') return 'bg-danger text-white'
		return 'bg-warning'
	}

	useEffect(() => {
		if (show && item?.id) getData()
	}, [show, item?.id]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal show={show} animation={false} centered scrollable size="lg" onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{t('webhook')}: {item?.name}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ 'height': '560px', 'overflowY': 'auto' }}>
				<div className="text-muted mb-3">{t('webhook_hint')}</div>

				<div className="form-group">
					<label>{t('webhook_url')}</label>
					<input type="text" className="form-control" placeholder="https://"
						value={webhook.url}
						onChange={(e) => setWebhook({ ...webhook, 'url': e.target.value })} />
				</div>

				<div className="form-group">
					<label>{t('webhook_events')}</label>
					<div className="small text-muted mb-1">{t('webhook_events_hint')}</div>
					{availableEvents.map(item => (
						<div className="form-check" key={item.event}>
							<input type="checkbox" className="form-check-input" id={`webhook-${item.event}`}
								disabled={!item.is_allowed}
								checked={item.is_allowed && webhook.events.includes(item.event)}
								onChange={() => toggleEvent(item.event)} />
							<label className={`form-check-label ${item.is_allowed ? '' : 'text-muted'}`} htmlFor={`webhook-${item.event}`}>
								{t(EVENT_LABELS[item.event] || item.event)} <span className="text-muted">({item.event})</span>
								{!item.is_allowed &&
									<span className="small ms-1">— {t('webhook_event_needs_permission', { 'permission': t(PERMISSION_LABELS[item.permission] || item.permission) })}</span>
								}
							</label>
						</div>
					))}
				</div>

				<div className="d-flex justify-content-between mb-2">
					<div className="fw-500 vertical-center">{t('webhook_active')}</div>
					<div className="form-check form-switch form-switch-lg">
						<input type="checkbox" className="form-check-input"
							checked={!!webhook.is_active}
							onChange={(e) => setWebhook({ ...webhook, 'is_active': e.target.checked })} />
					</div>
				</div>

				{!!secret &&
					<div className="form-group">
						<label>{t('webhook_secret')}</label>
						<div className="input-group">
							<input type="text" className="form-control" readOnly value={secret} />
							<button type="button" className="btn btn-outline-primary"
								onClick={() => navigator.clipboard.writeText(secret)}>
								<i className="uil-copy-alt"></i>
							</button>
							<button type="button" className="btn btn-outline-danger"
								onClick={() => save(true)}>
								{t('webhook_regenerate_secret')}
							</button>
						</div>
						<div className="small text-muted mt-1">{t('webhook_secret_hint')}</div>
					</div>
				}

				<div className="d-flex gap-2 mb-3">
					<button type="button" className="btn btn-primary" onClick={() => save(false)}>
						{t('save')}
					</button>
					<button type="button" className="btn btn-outline-primary" disabled={!secret}
						onClick={() => ping()}>
						{t('webhook_ping')}
					</button>
				</div>

				<h6>{t('webhook_deliveries')}</h6>
				<div className="table-responsive">
					<table className="table table-bordered mb-0 fz13">
						<thead>
							<tr>
								<th>{t('date')}</th>
								<th>{t('webhook_event')}</th>
								<th className="text-center">{t('status')}</th>
								<th className="text-center">{t('webhook_attempts')}</th>
								<th>{t('webhook_response')}</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{deliveries.map(delivery => (
								<tr key={delivery.id}>
									<td className="text-nowrap">{formatDate(delivery.created_at, 'dd.MM.yy HH:mm:ss')}</td>
									<td>{t(EVENT_LABELS[delivery.event] || delivery.event)}</td>
									<td className={`text-center ${returnClassStatus(delivery)}`}>
										{t('webhook_status_' + delivery.status)}
									</td>
									<td className="text-center">{delivery.attempts}</td>
									<td className="text-break" style={{ 'maxWidth': '260px' }}>
										{delivery.last_http_code ? `HTTP ${delivery.last_http_code}` : ''}
										{!!delivery.last_error && <div className="small text-danger">{delivery.last_error}</div>}
									</td>
									<td className="text-center">
										{delivery.status !== 'sent' &&
											<button type="button" className="btn btn-sm btn-outline-primary"
												onClick={() => retry(delivery)}>
												{t('webhook_retry')}
											</button>
										}
									</td>
								</tr>
							))}
							{!deliveries.length &&
								<tr>
									<td colSpan={6} className="text-center text-muted">{t('list_empty')}</td>
								</tr>
							}
						</tbody>
					</table>
				</div>
			</Modal.Body>
		</Modal>
	)
}

export default IntegrationKeyWebhook
